import { addDays, type LocalDate } from '../dates';
import {
  countsInSpending,
  inRange,
  spendAmount,
  type DateRange,
  type LedgerEntryLike,
} from '../ledger/counting';
import { comingIn, type ComingInItem, type ExpectedMoneyLike } from './expected';
import type { EffectivePayday } from './paydays';

export type PeriodTense = 'past' | 'current' | 'future';

export function tenseOf(range: DateRange, today: LocalDate): PeriodTense {
  if (range.end < today) return 'past';
  if (range.start > today) return 'future';
  return 'current';
}

/**
 * One payment due in the period (a bill, a loan payment, an installment, a utility).
 * Payments (v0.5) provides these through a commitments provider; Budget never reads Payments tables.
 */
export interface CommitmentItem {
  id: string;
  label: string;
  date: LocalDate;
  amountMinor: number;
  /** Utility estimates are labeled "estimated" until the actual amount is in. */
  estimated: boolean;
  paid: boolean;
  /** The ledger row this payment wrote when paid, so "Other spending" doesn't count it twice. */
  ledgerEntryId: string | null;
  /**
   * An unpaid due whose date has passed: dated today, so it's held back in the current period
   * (and no longer in its own past one) until it's paid or closed.
   */
  overdue?: boolean;
}

/** One cash-flow line such as "Fixed bills" or "Loans". `key` is stable ('bills', 'loans', 'installments', 'utilities'). */
export interface CommitmentLine {
  key: string;
  label: string;
  items: CommitmentItem[];
  /**
   * v0.5 cash view (Safe to spend from the money in your accounts): 'cashflow' lines count only in
   * the cash flow (card installments and card balances from before Tahi: card charges, not cash
   * leaving yet), 'cash' lines only in the cash view (a card statement's unpaid rest: cash that
   * will leave an account). Absent = both.
   */
  basis?: 'cashflow' | 'cash';
}

/**
 * Money the user plans to spend but hasn't yet, e.g. open Shopping list items with an estimate
 * (spec §6 "Planned spending"). Shown next to What's left, never subtracted from it until bought.
 * A module registers a planned-spending provider (apps/web data/commitments.ts) to add one.
 */
export interface PlannedItem {
  id: string;
  label: string;
  amountMinor: number;
}

/** One planned line, e.g. key 'shopping', label 'On your shopping list'. */
export interface PlannedLine {
  key: string;
  label: string;
  items: PlannedItem[];
}

export type IncomeItem =
  | {
      kind: 'payday';
      id: string;
      label: string;
      date: LocalDate;
      status: EffectivePayday['status'];
      defaultMinor: number;
      receivedMinor: number | null;
      /** What this payday adds to the period's income: received, the default if still expected, 0 if skipped. */
      countedMinor: number;
      /** v0.5: which payday of a twice-a-month salary ("mid-month", "end of month"). */
      cutoff?: 'mid-month' | 'end of month';
    }
  | {
      kind: 'other';
      id: string;
      label: string | null;
      date: LocalDate;
      receivedMinor: number;
      countedMinor: number;
    };

export interface CashFlowLine {
  key: string;
  label: string;
  totalMinor: number;
  items: CommitmentItem[];
}

export interface PlannedTotal {
  key: string;
  label: string;
  totalMinor: number;
  items: PlannedItem[];
}

export interface CashFlow {
  range: DateRange;
  tense: PeriodTense;
  /** Default amounts of every salary cutoff and bonus scheduled in the period (not an average). */
  expectedIncomeMinor: number;
  /** Income that actually came in: received paydays plus other income. */
  receivedIncomeMinor: number;
  /** What the period's received paydays brought (salary and bonuses only), to compare with expectedIncomeMinor. */
  paydaysReceivedMinor: number;
  /** Default amounts of the paydays received in the period: like-for-like with paydaysReceivedMinor. */
  paydaysReceivedDefaultMinor: number;
  /** One-off income with no payday behind it. */
  otherIncomeMinor: number;
  /** Defaults of paydays not confirmed yet, counted in current and future periods (0 for past ones). */
  pendingIncomeMinor: number;
  /** The income the period is worked out with: receivedIncomeMinor + pendingIncomeMinor. */
  incomeMinor: number;
  incomeItems: IncomeItem[];
  commitments: CashFlowLine[];
  commitmentsMinor: number;
  leftAfterCommitmentsMinor: number;
  /** Spending in the period that isn't one of the commitment payments above. */
  otherSpendingMinor: number;
  otherSpendingIds: string[];
  whatsLeftMinor: number;
  /** Planned spending shown beside What's left (current period only); not subtracted. */
  planned: PlannedTotal[];
  plannedMinor: number;
  /** v0.5: expected money (vouchers, a leave conversion) for the period; never in any total above. */
  comingIn: ComingInItem[];
  comingInMinor: number;
  /**
   * v0.6 "To savings": income in the period that lands in a savings-only account (a bonus that goes
   * straight to savings). It stays in income but is taken off What's left, so the cutoff is honest.
   */
  toSavings: IncomeItem[];
  toSavingsMinor: number;
}

export interface CashFlowInput {
  range: DateRange;
  today: LocalDate;
  /**
   * Effective paydays of the period and around it (paydaysInRange). Their income rows never count
   * as other income, wherever they fall.
   */
  paydays: readonly EffectivePayday[];
  /**
   * Which day puts a payday in the period: 'date' (the day it came; month views, the default) or
   * 'scheduled' (its expectedDate; cutoff views, so an early salary stays in its own cutoff).
   */
  paydaysBy?: 'date' | 'scheduled';
  /** Ledger rows; anything outside the range or not counted is ignored. */
  entries: readonly LedgerEntryLike[];
  /** From the registered commitments providers; empty until Payments ships (v0.5). */
  commitments: readonly CommitmentLine[];
  /** From the registered planned-spending providers (Shopping list); empty until it ships. */
  planned?: readonly PlannedLine[];
  /** v0.5 "Coming in": expected money that isn't income. Listed only, never added to a total. */
  expected?: readonly ExpectedMoneyLike[];
  /** v0.6: ids of paydays and income rows that land in a savings-only account (incomeToSavings). */
  toSavingsIds?: ReadonlySet<string>;
}

/** "Salary (mid-month)", "Salary (end of month)"; other paydays by their source's name. */
export function paydayLabel(p: Pick<EffectivePayday, 'sourceName' | 'cutoff'>): string {
  return p.cutoff ? `${p.sourceName} (${p.cutoff})` : p.sourceName;
}

const byDate = <T extends { date: LocalDate }>(a: T, b: T) =>
  a.date < b.date ? -1 : a.date > b.date ? 1 : 0;

/**
 * The "What's left" breakdown (spec, Income and the monthly picture) for any period, past, current
 * or future. Works with no commitments at all (then it is income minus spending).
 * Income used: past periods use received amounts only; current and future periods add the default
 * amount of every payday not confirmed yet (defaults, never averages).
 */
export function computeCashFlow(input: CashFlowInput): CashFlow {
  const { range, today } = input;
  const tense = tenseOf(range, today);
  const byScheduled = input.paydaysBy === 'scheduled';
  const paydays = input.paydays.filter((p) =>
    inRange(byScheduled ? p.expectedDate : p.date, range),
  );
  const incomeRows = input.entries.filter(
    (e) => e.kind === 'income' && e.deletedAt === null && inRange(e.date, range),
  );
  const paydayLedgerIds = new Set(
    input.paydays.map((p) => p.ledgerEntryId).filter((id): id is string => id !== null),
  );

  const incomeItems: IncomeItem[] = paydays.map((p) => {
    const receivedMinor = p.status === 'received' ? p.amountMinor : null;
    const countedMinor =
      p.status === 'received'
        ? (p.amountMinor ?? 0)
        : p.status === 'skipped' || tense === 'past'
          ? 0
          : p.defaultAmountMinor;
    return {
      kind: 'payday',
      id: p.id,
      label: paydayLabel(p),
      ...(p.cutoff ? { cutoff: p.cutoff } : {}),
      date: p.date,
      status: p.status,
      defaultMinor: p.defaultAmountMinor,
      receivedMinor,
      countedMinor,
    };
  });
  for (const e of incomeRows) {
    if (paydayLedgerIds.has(e.id)) continue;
    incomeItems.push({
      kind: 'other',
      id: e.id,
      label: e.note,
      date: e.date,
      receivedMinor: e.amountMinor,
      countedMinor: e.amountMinor,
    });
  }
  incomeItems.sort(byDate);

  const expectedIncomeMinor = paydays
    .filter((p) => p.status !== 'skipped')
    .reduce((s, p) => s + p.defaultAmountMinor, 0);
  const receivedPaydays = paydays.filter((p) => p.status === 'received');
  const paydaysReceivedMinor = receivedPaydays.reduce((s, p) => s + (p.amountMinor ?? 0), 0);
  const paydaysReceivedDefaultMinor = receivedPaydays.reduce((s, p) => s + p.defaultAmountMinor, 0);
  const otherIncomeMinor = incomeRows
    .filter((e) => !paydayLedgerIds.has(e.id))
    .reduce((s, e) => s + e.amountMinor, 0);
  const receivedIncomeMinor = paydaysReceivedMinor + otherIncomeMinor;
  const pendingIncomeMinor =
    tense === 'past'
      ? 0
      : paydays
          .filter((p) => p.status === 'expected')
          .reduce((s, p) => s + p.defaultAmountMinor, 0);
  const incomeMinor = receivedIncomeMinor + pendingIncomeMinor;

  const commitments: CashFlowLine[] = input.commitments
    .filter((line) => line.basis !== 'cash')
    .map((line) => {
      const items = line.items.filter((i) => inRange(i.date, range));
      return {
        key: line.key,
        label: line.label,
        items,
        totalMinor: items.reduce((s, i) => s + i.amountMinor, 0),
      };
    });
  const commitmentsMinor = commitments.reduce((s, l) => s + l.totalMinor, 0);
  const covered = new Set(
    commitments
      .flatMap((l) => l.items.map((i) => i.ledgerEntryId))
      .filter((id): id is string => id !== null),
  );

  const other = input.entries.filter(
    (e) => countsInSpending(e) && inRange(e.date, range) && !covered.has(e.id),
  );
  const otherSpendingMinor = other.reduce((s, e) => s + spendAmount(e), 0);
  const leftAfterCommitmentsMinor = incomeMinor - commitmentsMinor;

  const planned: PlannedTotal[] =
    tense === 'current'
      ? (input.planned ?? [])
          .map((l) => ({
            key: l.key,
            label: l.label,
            items: l.items,
            totalMinor: l.items.reduce((s, i) => s + i.amountMinor, 0),
          }))
          .filter((l) => l.totalMinor > 0)
      : [];

  const coming = comingIn(input.expected ?? [], range, today);
  const toSavings = incomeItems.filter(
    (i) => i.countedMinor > 0 && (input.toSavingsIds?.has(i.id) ?? false),
  );
  const toSavingsMinor = toSavings.reduce((s, i) => s + i.countedMinor, 0);

  return {
    range,
    tense,
    expectedIncomeMinor,
    receivedIncomeMinor,
    paydaysReceivedMinor,
    paydaysReceivedDefaultMinor,
    otherIncomeMinor,
    pendingIncomeMinor,
    incomeMinor,
    incomeItems,
    commitments,
    commitmentsMinor,
    leftAfterCommitmentsMinor,
    otherSpendingMinor,
    otherSpendingIds: other.map((e) => e.id),
    whatsLeftMinor: leftAfterCommitmentsMinor - otherSpendingMinor - toSavingsMinor,
    planned,
    plannedMinor: planned.reduce((s, l) => s + l.totalMinor, 0),
    comingIn: coming,
    comingInMinor: coming.reduce((s, i) => s + i.amountMinor, 0),
    toSavings,
    toSavingsMinor,
  };
}

/** Payments still to pay from today to the end of the range ("payments reserved"). */
export function reservedMinor(
  commitments: readonly CommitmentLine[],
  range: DateRange,
  today: LocalDate,
): number {
  const from = today > range.start ? today : range.start;
  return commitments
    .filter((l) => l.basis !== 'cash')
    .flatMap((l) => l.items)
    .filter((i) => !i.paid && i.date >= from && i.date <= range.end)
    .reduce((s, i) => s + i.amountMinor, 0);
}

export interface SafeToSpend {
  amountMinor: number;
  basis: 'fund' | 'cashflow';
  reservedMinor: number;
  /** The day the money has to last until (the day before the next refill or payday). */
  until: LocalDate;
  /** The first day of the next period ("until the 15th"). */
  nextStart: LocalDate;
}

/**
 * "Safe to spend" (spec): fund left minus payments reserved. Without a default fund, the current
 * period's cash flow, which already subtracts every payment due in the period.
 */
export function safeToSpend(
  input:
    | {
        basis: 'fund';
        leftMinor: number;
        range: DateRange;
        commitments: readonly CommitmentLine[];
        today: LocalDate;
      }
    | { basis: 'cashflow'; cashFlow: CashFlow },
): SafeToSpend {
  if (input.basis === 'fund') {
    const reserved = reservedMinor(input.commitments, input.range, input.today);
    return {
      amountMinor: input.leftMinor - reserved,
      basis: 'fund',
      reservedMinor: reserved,
      until: input.range.end,
      nextStart: addDays(input.range.end, 1),
    };
  }
  const cf = input.cashFlow;
  const reserved = cf.commitments
    .flatMap((l) => l.items)
    .filter((i) => !i.paid)
    .reduce((s, i) => s + i.amountMinor, 0);
  return {
    amountMinor: cf.whatsLeftMinor,
    basis: 'cashflow',
    reservedMinor: reserved,
    until: cf.range.end,
    nextStart: addDays(cf.range.end, 1),
  };
}

export interface CashSafeToSpend {
  basis: 'cash';
  amountMinor: number;
  /** "Your money": every account's balance now. */
  onHandMinor: number;
  /** Paydays that should have come by today but aren't confirmed yet (their usual amounts). */
  expectedMinor: number;
  /** Every unpaid payment due before the next payday, overdue ones included. */
  reservedMinor: number;
  due: CommitmentItem[];
  /** The day before the next payday. */
  until: LocalDate;
  /** The next payday. */
  nextStart: LocalDate;
}

/**
 * v0.5 "Safe to spend" from real money (Ivan, 2026-11-03): the money in your accounts now, plus
 * salary that should be in by today but isn't confirmed yet, minus every unpaid payment due
 * before the next payday (bills, utilities, loans, card statements, overdue ones dated today).
 * Card installments and card balances from before Tahi aren't counted on their own here: they are
 * inside the card statement, which is what leaves the account. "Coming in" extras never count.
 * v0.6: only accounts used for spending are "on hand" (the caller passes their total), and a
 * payday that lands in a savings-only account isn't expected money (the caller leaves it out).
 */
export function cashSafeToSpend(input: {
  onHandMinor: number;
  /** Unconfirmed paydays dated today or earlier (duePaydays). */
  duePaydays: readonly Pick<EffectivePayday, 'defaultAmountMinor' | 'status'>[];
  commitments: readonly CommitmentLine[];
  today: LocalDate;
  /** The next payday after today; null without income (then the next 30 days). */
  nextPayday: LocalDate | null;
}): CashSafeToSpend {
  const nextStart = input.nextPayday ?? addDays(input.today, 31);
  const expectedMinor = input.duePaydays
    .filter((p) => p.status === 'expected')
    .reduce((s, p) => s + p.defaultAmountMinor, 0);
  const due = input.commitments
    .filter((l) => l.basis !== 'cashflow')
    .flatMap((l) => l.items)
    .filter((i) => !i.paid && i.date >= input.today && i.date < nextStart)
    .sort(byDate);
  const reserved = due.reduce((s, i) => s + i.amountMinor, 0);
  return {
    basis: 'cash',
    amountMinor: input.onHandMinor + expectedMinor - reserved,
    onHandMinor: input.onHandMinor,
    expectedMinor,
    reservedMinor: reserved,
    due,
    until: addDays(nextStart, -1),
    nextStart,
  };
}
